import { useSplitClient as useSplitClientSDK } from '@splitsoftware/splitio-react';
import { useEffect, useState } from 'react';

interface UseSplitClientReturn {
  /** Has the split client finished loading */
  isReady: boolean;
  /** Split client returned from useSplitClient */
  splitClient: SplitIO.IBrowserClient | null;
}

/**
 * A cache to enable future calls to `useSplitClient` to be initially ready.
 * This will avoid a moment of the control treatment being provided if the
 * client is already loaded.
 */
export const cachedSplitClients: Record<string, boolean> = {};

/**
 * Wraps Split's useSplitClient in a custom hook to manage the isReady state.
 * Attaches to a cache variable to allow future calls to set isReady to
 * true to avoid a brief control treatment.
 *
 * @param splitKey - The key of the new client. Commonly the user or anonymous id.
 */
export const useSplitClient = (splitKey: string): UseSplitClientReturn => {
  const {
    client: splitClient,
    isReady: splitIsReady,
    hasTimedout,
  } = useSplitClientSDK({ splitKey });
  const [readyState, setReadyState] = useState(() => ({
    ...cachedSplitClients,
  }));

  useEffect(() => {
    if (!splitIsReady && !hasTimedout) {
      return;
    }

    // Always set to ready, this will skip the loading state to consumers.
    // If Split timed out will return control.
    setReadyState(prev => ({ ...prev, [splitKey]: true }));
    cachedSplitClients[splitKey] = true;
  }, [splitIsReady, hasTimedout, splitKey]);

  return {
    splitClient: splitClient ?? null,
    isReady: readyState[splitKey] ?? false,
  };
};
